import React, { useMemo } from "react";
import type { TranslationKey } from "../../../../api/useTranslations";
import { useTranslations } from "../../../../api/useTranslations";
import t from "../../../../i18n";
import createIOSortType from "../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useSortedData from "../../../../lib/hooks/useSortedData";
import type { SortableValue } from "diagnostics-lib/comparator";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { styled } from "@linaria/react";
import Ellipsis from "@ui/Ellipsis";
import { useCanUserDo } from "../../../canUserDo";
import Actions from "./Actions";
import { useModals } from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import UpdateTranslationModalForm from "./UpdateTranslationModalForm";
import { useTranslationTools } from "./useTranslationTools";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import { css } from "@linaria/core";
import classNames from "classnames";

const StyledActions = styled(Actions)`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
  }
`;

const widthConstraintTableCard = css`
  max-width: 1200px;
  height: auto;
`;

export type SettingModalType = "update";

const columns = ["key", "fr", "en"] as const;
type Column = (typeof columns)[number];
const useSortTranslation = createGlobalState({
  storage: "session",
  key: "translationSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "key",
    direction: "asc",
  },
  scoped: false,
});
interface P extends React.HtmlHTMLAttributes<HTMLDivElement> {
  className?: string;
}
interface Translation {
  key: TranslationKey;
  fr: string;
  en: string;
}

const sortSelector: { [key in Column]: (b: Translation) => SortableValue | null } = {
  key: b => b.key,
  fr: b => b.fr,
  en: b => b.en,
};
const computeRowKey = (translation: Translation) =>
  translation.key + " " + translation.fr + " " + translation.en;

const TranslationTable = ({ className }: P) => {
  const translations = useTranslations();
  const [sort, setSort] = useSortTranslation({});
  const { matchTranslationKey } = useTranslationTools();
  const canEdit = useCanUserDo("ADMIN_SETTING_MANAGE");
  const [translationData, setTranslationData] = React.useState<{
    impactedElement: TranslationKey;
    frenchWord: string;
    englishWord: string;
  }>();

  const { modal, closeModal, openModal } = useModals<SettingModalType>();
  const openEditModal = React.useCallback(
    (data: { impactedElement: TranslationKey; frenchWord: string; englishWord: string }) =>
      (e: React.MouseEvent) => {
        e.stopPropagation();
        openModal("update");
        setTranslationData(data);
      },
    [openModal],
  );

  const translationMetadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<Translation> } = {
      key: {
        label: t("customization.table.translation.key"),
        render: (t: Translation) => (
          <>
            <Ellipsis>{matchTranslationKey(t.key)}</Ellipsis>
            {canEdit && (
              <StyledActions
                openEditModal={openEditModal({
                  impactedElement: t.key as TranslationKey,
                  frenchWord: t.fr,
                  englishWord: t.en,
                })}
              />
            )}
          </>
        ),
        minWidth: "200px",
        maxWidth: "400px",
        sortable: true,
        sticky: true,
        showEmptyMark: true,
        alignment: "left",
      },
      fr: {
        label: t("customization.table.translation.fr"),
        render: (t: Translation) => t.fr,
        minWidth: "250px",
        maxWidth: "400px",
        sortable: false,
        showEmptyMark: true,
      },
      en: {
        label: t("customization.table.translation.en"),
        render: (t: Translation) => t.en,
        minWidth: "250px",
        maxWidth: "400px",
        sortable: false,
        showEmptyMark: true,
      },
    };
    return _;
  }, [canEdit, matchTranslationKey, openEditModal]);

  const datas = useMemo(
    () =>
      Object.keys(translations.EN).map(m => ({
        key: m as TranslationKey,
        fr: translations.FR[m as keyof typeof translations.FR],
        en: translations.EN[m as keyof typeof translations.EN],
      })),
    [translations],
  );

  const sortedDatas = useSortedData(datas, sortSelector[sort.column], sort.direction);

  return (
    <>
      <Modal
        title={t("customization.modal.edit.translation.title")}
        subtitle={t("customization.modal.edit.translation.subtitle")}
        isOpen={modal === "update"}
        onRequestClose={closeModal}
      >
        <UpdateTranslationModalForm
          onRequestClose={closeModal}
          data={translationData}
          okButtonName={t("modalForm.button.name.update")}
        />
      </Modal>
      <TableCard
        title={null}
        headerLeft={null}
        headerRight={null}
        className={classNames(className, widthConstraintTableCard)}
      >
        <Table
          stickyHeader
          datas={sortedDatas}
          metadata={translationMetadata}
          sort={sort}
          onSort={setSort}
          computeRowKey={computeRowKey}
          isRowInteractive={() => true}
          data-cy="translation-table"
        />
      </TableCard>
    </>
  );
};

export default TranslationTable;
